import React, { useState } from "react"
import { Sun, AlertTriangle, Clock, CalendarDays, Plus, Loader2, RefreshCw, CheckCircle2, ListTodo, AlarmClock } from "lucide-react"
import { AppShell } from "src/components/layout/AppShell"
import { Button } from "src/components/ui/button"
import { useToast } from "src/components/ui/toast"
import { TaskCard } from "src/features/tasks/components/TaskCard"
import { TaskFormDrawer } from "src/features/tasks/components/TaskFormDrawer"
import { useMyDay } from "src/features/tasks/hooks/useMyDay"
import { completeTask, reopenTask, deleteTask, snoozeTask, unsnoozeTask } from "src/features/tasks/api/tasksApi"
import { cn } from "src/utils/cn"

function Bucket({ icon: Icon, label, color, count, tasks, onComplete, onReopen, onDelete, onSnooze, onUnsnooze, onEdit }) {
  if (tasks.length === 0) return null
  return (
    <section>
      <div className="mb-2.5 flex items-center gap-2">
        <Icon className={cn("h-4 w-4", color)} />
        <h2 className={cn("text-[13px] font-semibold uppercase tracking-wide", color)}>{label}</h2>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">{count}</span>
      </div>
      <div className="space-y-2">
        {tasks.map((t) => (
          <TaskCard
            key={t.id}
            task={t}
            onComplete={onComplete}
            onReopen={onReopen}
            onDelete={onDelete}
            onSnooze={onSnooze}
            onUnsnooze={onUnsnooze}
            onEdit={onEdit}
            showContext
          />
        ))}
      </div>
    </section>
  )
}

export default function MyDayPage() {
  const { toast } = useToast()
  const { buckets, loading, refresh } = useMyDay()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  async function handleComplete(id) {
    try {
      await completeTask(id)
      toast({ title: "Task done!" })
      refresh()
    } catch {
      toast({ variant: "destructive", title: "Could not complete task" })
    }
  }

  async function handleReopen(id) {
    try {
      await reopenTask(id)
      toast({ title: "Task reopened" })
      refresh()
    } catch {
      toast({ variant: "destructive", title: "Could not reopen task" })
    }
  }

  async function handleDelete(id) {
    try {
      await deleteTask(id)
      toast({ title: "Task deleted" })
      refresh()
    } catch {
      toast({ variant: "destructive", title: "Could not delete task" })
    }
  }

  async function handleSnooze(id) {
    try {
      await snoozeTask(id)
      toast({ title: "Snoozed for 1 hour" })
      refresh()
    } catch {
      toast({ variant: "destructive", title: "Could not snooze" })
    }
  }

  async function handleUnsnooze(id) {
    try {
      await unsnoozeTask(id)
      toast({ title: "Snooze removed" })
      refresh()
    } catch {
      toast({ variant: "destructive", title: "Could not unsnooze" })
    }
  }

  function handleEdit(task) {
    setEditing(task)
    setDrawerOpen(true)
  }

  function handleSaved() {
    refresh()
  }

  const openCount = buckets.overdue.length + buckets.today.length + buckets.upcoming.length
  const completedCount = buckets.completed.length
  const snoozedCount = buckets.snoozed.length
  const now = new Date()
  const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 18 ? "Good afternoon" : "Good evening"

  return (
    <AppShell>
      <div className="px-8 py-7 space-y-5">

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <ListTodo className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-amber-500">
                <Sun className="h-4 w-4" />
                <span className="text-[13px] font-medium">{greeting}</span>
              </div>
              <h1 className="text-xl font-semibold text-slate-900 tracking-tight">My Day</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh}>
              <RefreshCw className="h-4 w-4 text-slate-500" />
            </Button>
            <Button variant="default" size="sm" className="gap-2" onClick={() => { setEditing(null); setDrawerOpen(true) }}>
              <Plus className="h-4 w-4" />
              New task
            </Button>
          </div>
        </div>

        {/* ── Summary ── */}
        <p className="text-[13px] text-slate-500">
          {openCount === 0 && completedCount === 0 && snoozedCount === 0
            ? "Nothing on your plate today"
            : `${openCount} open task${openCount !== 1 ? "s" : ""}${completedCount > 0 ? ` · ${completedCount} completed today` : ""}${snoozedCount > 0 ? ` · ${snoozedCount} snoozed` : ""}`}
        </p>

        {/* ── Content ── */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
          </div>
        ) : openCount === 0 && completedCount === 0 && snoozedCount === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center">
            <Sun className="mx-auto mb-3 h-10 w-10 text-amber-200" />
            <p className="text-slate-500">You're all caught up!</p>
            <p className="mt-0.5 text-sm text-slate-400">No tasks due today or overdue.</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => { setEditing(null); setDrawerOpen(true) }}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add a task
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <Bucket
              icon={AlertTriangle}
              label="Overdue"
              color="text-red-600"
              count={buckets.overdue.length}
              tasks={buckets.overdue}
              onComplete={handleComplete}
              onReopen={handleReopen}
              onDelete={handleDelete}
              onSnooze={handleSnooze}
              onEdit={handleEdit}
            />
            <Bucket
              icon={Clock}
              label="Today"
              color="text-blue-600"
              count={buckets.today.length}
              tasks={buckets.today}
              onComplete={handleComplete}
              onReopen={handleReopen}
              onDelete={handleDelete}
              onSnooze={handleSnooze}
              onEdit={handleEdit}
            />
            <Bucket
              icon={CalendarDays}
              label="Upcoming"
              color="text-slate-600"
              count={buckets.upcoming.length}
              tasks={buckets.upcoming}
              onComplete={handleComplete}
              onReopen={handleReopen}
              onDelete={handleDelete}
              onSnooze={handleSnooze}
              onEdit={handleEdit}
            />

            {/* ── Snoozed ── */}
            {buckets.snoozed.length > 0 && (
              <>
                <div className="border-t border-slate-100" />
                <Bucket
                  icon={AlarmClock}
                  label="Snoozed"
                  color="text-violet-600"
                  count={buckets.snoozed.length}
                  tasks={buckets.snoozed}
                  onComplete={handleComplete}
                  onReopen={handleReopen}
                  onDelete={handleDelete}
                  onUnsnooze={handleUnsnooze}
                  onEdit={handleEdit}
                />
              </>
            )}

            {/* ── Completed ── */}
            {buckets.completed.length > 0 && (
              <>
                <div className="border-t border-slate-100" />
                <Bucket
                  icon={CheckCircle2}
                  label="Completed today"
                  color="text-emerald-600"
                  count={buckets.completed.length}
                  tasks={buckets.completed}
                  onComplete={handleComplete}
                  onReopen={handleReopen}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                />
              </>
            )}
          </div>
        )}
      </div>

      <TaskFormDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null) }}
        onSaved={handleSaved}
        task={editing}
      />
    </AppShell>
  )
}
